import * as React from 'react'
import { UseAutocompleteProps, useAutocomplete } from '@mui/base/useAutocomplete'
import { Button } from '@mui/base/Button'
import { Popper } from '@mui/base/Popper'
import { Input } from '@mui/base/Input'
import { unstable_useForkRef as useForkRef } from '@mui/utils'
import { ReactComponent as ArrowDropDownIcon } from '@/assets/icons/chevron-right.svg'
import { ReactComponent as ClearIcon } from '@/assets/icons/trash.svg'
import clsx from 'clsx'
import { Option } from '@/components/ui/Autocomplete/Autocomplete.types'
import classes from './Autocomplete.module.scss'
import { useMemo } from 'react'

export type AutocompleteProps = Omit<UseAutocompleteProps<Option, false, boolean, false>, 'onChange' | 'value'> & {
  name: string
  value: any
  onChange: (field: string, value: any) => void
  selectOnly?: boolean
}

const Autocomplete = React.forwardRef<HTMLDivElement, AutocompleteProps>(function Autocomplete(props, ref) {
  const {
    disableClearable = false,
    disabled = false,
    readOnly = false,
    selectOnly = false,
    options,
    name,
    onChange,
    value,
    ...other
  } = props

  const optionValue = useMemo(() => options.find((option) => option.value === value), [value])

  const {
    getRootProps,
    getInputProps,
    getPopupIndicatorProps,
    getClearProps,
    getListboxProps,
    getOptionProps,
    dirty,
    id,
    popupOpen,
    focused,
    anchorEl,
    setAnchorEl,
    groupedOptions,
  } = useAutocomplete({
    ...props,
    value: optionValue,
    componentName: 'Autocomplete',
    onChange: (event, option) => onChange(name, option?.value),
  })

  const hasClearIcon = !disableClearable && !disabled && dirty && !readOnly

  const rootRef = useForkRef(ref, setAnchorEl)

  return (
    <React.Fragment>
      <div {...getRootProps(other)} ref={rootRef} className={clsx(classes.root, focused && 'focused')}>
        <Input
          id={id}
          ref={setAnchorEl}
          disabled={disabled}
          readOnly={readOnly || selectOnly}
          slotProps={{
            root: {
              className: classes.inputRoot,
            },
            input: {
              className: classes.input,
              ...getInputProps(),
            },
          }}
        />

        {hasClearIcon && (
          <Button {...getClearProps()} className={clsx(classes.indicator, classes.clearIndicator)}>
            <ClearIcon />
          </Button>
        )}
        <Button
          {...getPopupIndicatorProps()}
          className={clsx(classes.indicator, classes.popupIndicator, popupOpen && classes.popupOpen)}
        >
          <ArrowDropDownIcon />
        </Button>
      </div>
      {anchorEl ? (
        <Popper
          placement={'bottom-end'}
          open={popupOpen}
          anchorEl={anchorEl}
          slotProps={{
            root: {
              className: classes.popper,
              style: {
                width: anchorEl?.offsetWidth ?? 320,
              },
            },
          }}
          modifiers={[
            { name: 'flip', enabled: false },
            { name: 'preventOverflow', enabled: false },
          ]}
        >
          <ul {...getListboxProps()} className={classes.listbox}>
            {groupedOptions.map((item, index) => {
              const option = item as Option
              const optionProps = getOptionProps({ option, index })

              return (
                <li {...optionProps} key={option.value} className={classes.option}>
                  {option.label}
                </li>
              )
            })}

            {groupedOptions.length === 0 && <li className={classes.noOptions}>No results</li>}
          </ul>
        </Popper>
      ) : null}
    </React.Fragment>
  )
})

export default Autocomplete
